import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Alert } from '@televet/kibble-ui/Alert';
import { Spinner } from '@televet/kibble-ui/Spinner';
import { Flex } from '@televet/kibble-ui/chakra';
import { useEffect, useRef } from 'react';
import { useAstroPetTRPC } from '@/lib/trpcContext';
import { NutritionIntakeForm } from '@/features/nutritionIntake/NutritionIntakeForm';

/**
 * The campaign form landing page: `/forms/:formResponseId`.
 *
 * ⚠️ **The path param is the whole credential.** The link is token-less (`PublicShort`),
 * there is no pet-parent auth, and the id is an unguessable 22-character value minted per
 * send. Nothing else about the pet or clinic travels in the URL — `petName`, `clinicName`
 * and the phone number all come back from the server, deliberately unlike the inform
 * landing pages, which pass them as plaintext query params.
 *
 * The route is generic over campaigns because `FormResponse` is (D23): the row's `formKey`
 * says which form this is. Only `nutrition-intake` exists today, so the route renders it
 * directly; a second form is the point at which this should dispatch on `formKey` rather
 * than growing conditionals.
 *
 * This component does resolution only — load, errors, and the funnel stamp. The step machine
 * lives in `NutritionIntakeForm`.
 */

/**
 * `?preview=1` loads the form without recording that anyone saw it.
 *
 * The admin's link into this page uses it. Without it an operator opening a parent's form writes
 * two stamps that claim the parent did something: `firstPetParentInteractionAt` from the mutation
 * below, and `recommendationShownAt` from the load. Both then misreport the funnel for a parent
 * who may never have reached the screen.
 *
 * It also makes the form read-only. Navigation still works, so every screen and every answer can
 * be read, but step saves apply to local state only and Submit and Confirm are disabled. A reader
 * cannot overwrite the parent's answers, submit on their behalf, or trigger the appointment-note
 * writeback.
 *
 * ⚠️ Client-side only. These endpoints are unauthenticated, so the server cannot tell a preview
 * from a real load and nothing here is a security boundary — it stops an operator writing by
 * accident, which is the actual risk.
 */
const formSearchSchema = z
  .object({
    /**
     * ⚠️ `z.unknown()`, and it must stay that way. The router JSON-parses search values, so
     * `?preview=1` arrives as the NUMBER 1, `?preview=true` as a boolean, and a hand-typed one as
     * a string. A schema that accepts only some of those THROWS in `validateSearch` and the route
     * renders nothing at all — the form does not load. Accept anything, decide here, never throw.
     *
     * Not `z.coerce.boolean()` either: `Boolean("0")` is true, so `?preview=0` would read as
     * preview. Only the values that mean yes count; anything else is a normal load.
     */
    preview: z
      .unknown()
      .transform((value) => value === true || value === 1 || value === '1' || value === 'true'),
  })
  // Keeps tracking params a mail client may append, matching `inviteSearchSchema`.
  .passthrough();

function FormRoute(): React.JSX.Element {
  const { formResponseId } = Route.useParams();
  const { preview } = Route.useSearch();
  const trpc = useAstroPetTRPC();

  const {
    data: form,
    isPending,
    isError,
    error,
    refetch,
  } = useQuery(trpc.nutritionForm.get.queryOptions({ formResponseId, preview }));

  const { mutate: stampStarted } = useMutation(trpc.nutritionForm.start.mutationOptions());

  /**
   * The funnel "Started" stamp, once per mount and only after the form has loaded — a
   * stamp before the load would count parents whose link is broken.
   *
   * A mutation rather than a side effect of the query on purpose: tRPC sends queries as GET,
   * and email security scanners and privacy proxies (Apple MPP, Proofpoint) prefetch links.
   * The existing unsubscribe route splits GET from POST for the same reason.
   */
  const hasStamped = useRef(false);
  useEffect(() => {
    if (form === undefined || hasStamped.current || preview === true) return;
    hasStamped.current = true;
    stampStarted({ formResponseId });
    // Depends on `mutate` rather than the whole mutation object: including the object would
    // re-run the effect on every status change. `mutate` is referentially stable across
    // renders, so the dependency list can be complete without that. The ref still guards
    // double-invocation under StrictMode.
  }, [form, formResponseId, preview, stampStarted]);

  if (isPending) {
    return (
      <Flex justify="center" py={12}>
        <Spinner size="lg" />
      </Flex>
    );
  }

  if (isError || form === undefined) {
    // An expired link comes back as FORBIDDEN with its own copy, so retrying it would only
    // fail again — the server's message is shown either way and the retry is offered only
    // for errors that might actually be transient.
    const isExpired = error?.data?.code === 'FORBIDDEN';
    return (
      <Flex justify="center" py={12} px={3}>
        <Alert
          status="error"
          title={error?.message ?? 'We could not load this form.'}
          description={
            isExpired
              ? undefined
              : 'Please try the link from your message again, or call your clinic.'
          }
          hideCloseButton
          buttonProps={
            isExpired ? undefined : { buttonText: 'Retry', onClick: () => void refetch() }
          }
        />
      </Flex>
    );
  }

  return (
    <NutritionIntakeForm
      formResponseId={formResponseId}
      species={form.species}
      petName={form.petName}
      clinicName={form.clinicName}
      clinicPhoneNumber={form.clinicPhoneNumber}
      clinicEmail={form.clinicEmail}
      answers={form.answers}
      isComplete={form.isComplete}
      prior={form.prior}
      outcome={form.outcome}
      sharesRecommendationWithClient={form.addOns.scienceDietRecommendations}
      preview={preview}
    />
  );
}

export const Route = createFileRoute('/forms/$formResponseId')({
  component: FormRoute,
  validateSearch: formSearchSchema,
});
